import { concat, filter, forEach, includes, isEmpty, map } from 'lodash';

import { LAYER_SUBTYPES } from '../api/Layers/Layers';

import { loadXMLFromURL } from './load-xml-from-url';

const EPSG_4326 = 'EPSG:4326';

/**
 * Fetch the bounding box of one layer for a WMS layer stored in the URL.
 *
 * @async
 * @param {object} arg - The arguments object.
 * @param {string} arg.url - The URL where the WMS is stored.
 * @param {string} arg.wmsLayerNames - The names of the stored WMS layers.
 * @param {string} arg.connectionParams - The user-password needed for authentication.
 * @returns {Promise<number[]>} The WMS layer Bounding Box.
 * @throws {Error} If something goes wrong.
 */
const loadWMSBBoxFromURL = async ({ url, wmsLayerNames, connectionParams }) => {
  try {
    const xmlText = await loadXMLFromURL(url, LAYER_SUBTYPES.WMS, connectionParams);
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlText, 'text/xml');
    const layers = xmlDoc.getElementsByTagName('Layer');
    const wmsLayerNamesArray = wmsLayerNames.split(',');

    const targetLayers = filter(
      layers,
      layer =>
        layer.hasAttribute('queryable') &&
        includes(wmsLayerNamesArray, layer.getElementsByTagName('Name')[0].innerHTML.trim())
    );

    let innerBBoxes = [];
    forEach(targetLayers, targetLayer => {
      const bboxes = targetLayer.getElementsByTagName('BoundingBox');
      concat(
        innerBBoxes,
        filter(bboxes, bbox => bbox.getAttribute('CRS') === EPSG_4326)
      );
    });

    if (isEmpty(innerBBoxes) && !isEmpty(layers)) {
      const bboxes = layers[0].getElementsByTagName('BoundingBox');
      innerBBoxes = filter(bboxes, bbox => bbox.getAttribute('CRS') === EPSG_4326);
    }

    const initialBBminx = map(innerBBoxes, innerBBox => Number(innerBBox.getAttribute('minx')));
    const initialBBminy = map(innerBBoxes, innerBBox => Number(innerBBox.getAttribute('miny')));
    const initialBBmaxx = map(innerBBoxes, innerBBox => Number(innerBBox.getAttribute('maxx')));
    const initialBBmaxy = map(innerBBoxes, innerBBox => Number(innerBBox.getAttribute('maxy')));

    let wmsBBox = [
      Math.min(...initialBBminx),
      Math.min(...initialBBminy),
      Math.max(...initialBBmaxx),
      Math.max(...initialBBmaxy)
    ];
    if (includes(wmsBBox, Infinity)) wmsBBox = [-180, -90, 180, 90];

    return wmsBBox;
  } catch (error) {
    throw new Error(error.message);
  }
};

export default loadWMSBBoxFromURL;
